import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { Home, Store, Plus, User, MessageSquare, Megaphone, Package } from "lucide-react";

export const BottomNav = () => {
  const { user } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [unread, setUnread] = useState(0);

  const isSeller = user?.role === "seller";
  const isBuyer = user?.role === "buyer";
  const isAdmin = user?.role === "admin";
  const chatUser = isSeller || isBuyer;

  const loadUnread = useCallback(() => {
    if (!chatUser) return;
    api.get("/conversations/unread_count").then((r) => setUnread(r.data.count || 0)).catch(() => {});
  }, [chatUser]);

  useEffect(() => { loadUnread(); }, [loadUnread, pathname]);
  useEffect(() => {
    if (!chatUser) return;
    const id = setInterval(loadUnread, 15000);
    return () => clearInterval(id);
  }, [chatUser, loadUnread]);

  const myspace = !user ? "/connexion" : isAdmin ? "/admin" : "/mon-espace";

  // Left two tabs
  const left = [
    { key: "home", label: t("nav.home"), Icon: Home, to: "/", active: pathname === "/" },
    { key: "market", label: t("nav.market"), Icon: Store, to: "/catalogue", active: pathname.startsWith("/catalogue") },
  ];

  // Right two tabs
  const right = [];
  if (chatUser) {
    right.push({ key: "messages", label: t("nav.messages"), Icon: MessageSquare, to: "/echanges", active: pathname.startsWith("/echanges"), badge: unread });
  } else if (isAdmin) {
    right.push({ key: "activity", label: t("nav.activity"), Icon: Package, to: "/admin", active: pathname === "/admin" });
  } else {
    right.push({ key: "login", label: t("nav.login"), Icon: MessageSquare, to: "/connexion", active: false });
  }
  right.push({ key: "myspace", label: t("nav.myspace"), Icon: User, to: myspace, active: pathname === "/mon-espace" || (isAdmin && pathname === "/admin") });

  const Tab = (tab) => (
    <button key={tab.key} onClick={() => navigate(tab.to)} data-testid={`tab-${tab.key}`}
      className={`relative flex flex-col items-center justify-center gap-0.5 h-full ${tab.active ? "text-primary" : "text-muted-foreground"}`}>
      <span className="relative">
        <tab.Icon className="h-6 w-6" strokeWidth={tab.active ? 2.4 : 2} />
        {tab.badge > 0 && (
          <span data-testid="messages-unread-badge"
            className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center leading-none">
            {tab.badge > 9 ? "9+" : tab.badge}
          </span>
        )}
      </span>
      <span className="text-[10px] font-semibold">{tab.label}</span>
    </button>
  );

  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-card border-t border-border" data-testid="bottom-nav">
      <div className="relative grid grid-cols-5 items-end h-16 max-w-lg mx-auto px-1">
        {left.map(Tab)}
        {/* center: Publier FAB (sellers) or spacer */}
        {isSeller ? (
          <div className="flex justify-center">
            <button onClick={() => navigate("/publier")} data-testid="tab-publish"
              className="absolute -top-6 h-16 w-16 rounded-full bg-primary text-primary-foreground shadow-xl ring-4 ring-card flex flex-col items-center justify-center active:scale-95 transition-transform">
              <Plus className="h-7 w-7" strokeWidth={2.6} />
              <span className="text-[9px] font-bold">{t("nav.publish")}</span>
            </button>
          </div>
        ) : isBuyer ? (
          Tab({ key: "offers", label: t("nav.offers"), Icon: Megaphone, to: "/offres", active: pathname.startsWith("/offres") })
        ) : (
          <div />
        )}
        {right.map(Tab)}
      </div>
    </nav>
  );
};
